import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Clock3,
  ArrowLeft,
  ArrowRight,
  Star,
  Dumbbell,
  Package,
  Users,
} from "lucide-react";
import {
  mpGetGymDetail,
  mpGetTrainers,
  mpGetPackages,
} from "../../../../services/marketplaceService";
import ImageWithFallback from "../../../common/ImageWithFallback";
import { getCurrentUser } from "../../../../utils/auth";
import PublicFeedbackSection from "../../../common/PublicFeedbackSection";
import "./GymDetailsPage.css";

const Stat = ({ icon, label, value }) => (
  <div className="gd-stat">
    <div className="gd-statIcon">{icon}</div>
    <div className="gd-statBody">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  </div>
);

const Card = ({ title, right, children }) => (
  <div className="gd-card">
    <div className="gd-cardHead">
      <h3>{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

export default function GymDetailsPage() {
  const { gymId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [gym, setGym] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = getCurrentUser();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [gymRes, trainerRes, packageRes] = await Promise.all([
          mpGetGymDetail(gymId),
          mpGetTrainers({ gymId }),
          mpGetPackages({ gymId }),
        ]);
        if (!mounted) return;

        setGym(gymRes.data?.DT || null);
        setTrainers(Array.isArray(trainerRes.data?.DT?.items) ? trainerRes.data.DT.items : Array.isArray(trainerRes.data?.DT) ? trainerRes.data.DT : []);
        setPackages(Array.isArray(packageRes.data?.DT?.items) ? packageRes.data.DT.items : Array.isArray(packageRes.data?.DT) ? packageRes.data.DT : []);
      } finally {
        setLoading(false);
      }
    })();

    return () => (mounted = false);
  }, [gymId]);

  const cover = useMemo(() => {
    const imgs = gym?.images;
    if (Array.isArray(imgs) && imgs.length) return imgs[0];
    return "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1600&auto=format&fit=crop";
  }, [gym]);

  const status = String(gym?.status || "").toLowerCase();
  const statusLabel =
    status === "active"
      ? "ĐANG HOẠT ĐỘNG"
      : status === "inactive"
      ? "NGỪNG HOẠT ĐỘNG"
      : status === "suspended"
      ? "TẠM KHÓA"
      : "N/A";

  const requireLoginThenGoBooking = () => {
    if (!user) {
      sessionStorage.setItem(
        "redirectAfterLogin",
        `${location.pathname}${location.search}`
      );
      navigate("/login");
      return;
    }

    navigate(`/member/booking/wizard?gymId=${gym.id}`);
  };

  if (loading)
    return (
      <div className="gd-page">
        <div className="gd-container">
          <div className="gd-loading">Đang tải gym...</div>
        </div>
      </div>
    );

  if (!gym || status === "suspended")
    return (
      <div className="gd-page">
        <div className="gd-container">
          <div className="gd-empty">Không tìm thấy gym</div>
        </div>
      </div>
    );

  return (
    <div className="gd-page">
      <div className="gd-container">
        <section className="gd-hero">
          <div className="gd-heroMedia">
            <img src={cover} alt={gym.name} />
            <div className="gd-heroOverlay" />
          </div>

          <button
            className="gd-back"
            onClick={() =>
              window.history.length > 1 ? navigate(-1) : navigate("/marketplace/gyms")
            }
          >
            <ArrowLeft size={18} />
            <span>Quay lại</span>
          </button>

          <div className="gd-heroContent">
            <div className="gd-heroLeft">
              <div className="gd-breadcrumb">DANH SÁCH CƠ SỞ</div>
              <h1 className="gd-title">
                {gym.name}
              </h1>

              <div className="gd-sub">
                <MapPin size={16} />
                <span>{gym.address || "—"}</span>
              </div>

              <div className="gd-badges">
                <span className={`gd-status ${status || "unknown"}`}>{statusLabel}</span>
                {gym.operatingHours && (
                  <span className="gd-chip">
                    <Clock3 size={14} />
                    <span>{gym.operatingHours}</span>
                  </span>
                )}
                {gym.phone && (
                  <span className="gd-chip">
                    <Phone size={14} />
                    <span>{gym.phone}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="gd-heroRight">
              <button className="gd-cta" onClick={requireLoginThenGoBooking}>
                Đặt lịch ngay
              </button>

              <button
                className="gd-cta"
                onClick={() => navigate(`/member/membership-cards?gymId=${gym.id}`)}
              >
                Mua thẻ thành viên
              </button>

              <button
                className="gd-cta ghost"
                onClick={() => navigate(`/marketplace/trainers?gymId=${gym.id}`)}
              >
                Xem PT của gym
              </button>
            </div>
          </div>

          <div className="gd-bgText">GFMS</div>
        </section>

        <section className="gd-stats">
          <Stat icon={<Users size={18} />} label="Huấn luyện viên" value={trainers.length} />
          <Stat icon={<Package size={18} />} label="Gói tập" value={packages.length} />
          <Stat
            icon={<Star size={18} />}
            label="Đánh giá"
            value={`${Number(gym.avgRating || 0).toFixed(1)} (${Number(gym.reviewCount || 0)})`}
          />
          <Stat icon={<Phone size={18} />} label="Liên hệ" value={gym.phone || "—"} />
        </section>

        <section className="gd-grid">
          <div className="gd-left">
            <Card title="Giới thiệu">
              <div className="gd-text">{gym.description || "Gym chưa cập nhật mô tả."}</div>
            </Card>

            <Card
              title="Huấn luyện viên"
              right={trainers.length ? <span className="gd-muted">{trainers.length} PT</span> : null}
            >
              {trainers.length ? (
                <div className="gd-list">
                  {trainers.map((t) => (
                    <div
                      key={t.id}
                      className="gd-item"
                      onClick={() => navigate(`/marketplace/trainers/${t.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && navigate(`/marketplace/trainers/${t.id}`)
                      }
                    >
                      <div className="gd-ava">
                        <ImageWithFallback
                          src={t.User?.avatar}
                          alt={t.User?.username}
                          fallback="/placeholder-pt.jpg"
                        />
                      </div>

                      <div className="gd-itemInfo">
                        <div className="gd-itemTitle">{t.User?.username || "PT"}</div>
                        <div className="gd-itemMeta">
                          <Star size={14} fill="currentColor" />
                          <span>{Number(t.avgRating || t.rating || 0).toFixed(1)}</span>
                          <span>({Number(t.reviewCount || 0)})</span>
                          <span>•</span>
                          <span>{t.specialization || "Personal Trainer"}</span>
                        </div>
                      </div>

                      <span className="gd-itemGo">
                        <ArrowRight size={18} />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="gd-muted">Chưa có huấn luyện viên.</div>
              )}
            </Card>

            <Card
              title="Gói tập"
              right={packages.length ? <span className="gd-muted">{packages.length} gói</span> : null}
            >
              {packages.length ? (
                <div className="gd-pkgs">
                  {packages.map((p) => (
                    <div
                      key={p.id}
                      className="gd-pkg"
                      onClick={() => navigate(`/marketplace/packages/${p.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && navigate(`/marketplace/packages/${p.id}`)
                      }
                    >
                      <div className="gd-pkgTop">
                        <div className="gd-pkgName">{p.name}</div>
                        <div className="gd-pkgPrice">
                          {Number(p.price || 0).toLocaleString("vi-VN")}đ
                        </div>
                      </div>

                      <div className="gd-pkgMeta">
                        <span className="gd-miniTag">
                          <Users size={14} />
                          <span>PT chung toàn gym</span>
                        </span>
                        <span className="gd-miniTag">
                          <Dumbbell size={14} />
                          <span>{p.sessions} buổi</span>
                        </span>
                        <span className="gd-miniTag">
                          <Clock3 size={14} />
                          <span>{p.durationDays} ngày</span>
                        </span>
                        <span className="gd-miniTag">
                          <Package size={14} />
                          <span>{p.type || "basic"}</span>
                        </span>
                      </div>

                      <div className="gd-pkgAction">Xem chi tiết</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="gd-muted">Chưa có gói tập.</div>
              )}
            </Card>

            <PublicFeedbackSection
              title="Feedback"
              subtitle="Các đánh giá nổi bật của hội viên về gym và các gói tập thuộc cơ sở này."
              items={gym.feedback || []}
              className="gd-feedbackSection"
            />
          </div>

          <div className="gd-right">
            <Card title="Thông tin liên hệ">
              <div className="gd-contactList">
                <div className="gd-contactItem">
                  <span className="gd-contactIcon">
                    <Phone size={16} />
                  </span>
                  <span>{gym.phone || "—"}</span>
                </div>

                <div className="gd-contactItem">
                  <span className="gd-contactIcon">
                    <Mail size={16} />
                  </span>
                  <span>{gym.email || "—"}</span>
                </div>

                <div className="gd-contactItem">
                  <span className="gd-contactIcon">
                    <MapPin size={16} />
                  </span>
                  <span>{gym.address || "—"}</span>
                </div>
              </div>
            </Card>

            <Card
              title="Ảnh gym"
              right={
                Array.isArray(gym.images) && gym.images.length ? (
                  <span className="gd-muted">{gym.images.length} ảnh</span>
                ) : null
              }
            >
              {Array.isArray(gym.images) && gym.images.length ? (
                <div className="gd-gallery">
                  {gym.images.slice(0, 6).map((url, i) => (
                    <div className="gd-galleryItem" key={i}>
                      <img src={url} alt={`Ảnh gym ${i + 1}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="gd-muted">Chưa có ảnh.</div>
              )}

              <button className="gd-cta2" onClick={requireLoginThenGoBooking}>
                Đặt lịch ngay
              </button>

              <div className="gd-ctaNote">
                Chọn Gói tập, PT và lịch tập trong bước tiếp theo.
              </div>
            </Card>
          </div>
        </section>

      </div>
    </div>
  );
}